import { signal } from '@preact/signals';
import type { MatchResult, MatchSettings, PlayerInfo, ScoreLine } from '@vm/shared';

export interface KillFeedEntry {
  id: number;
  killer: string;
  killerTeam: number;
  victim: string;
  victimTeam: number;
  weapon: string;
  flags: number;
  time: number;
  involvesMe: boolean;
  /** The kill was made by a pet (weapon shows the pet's name). */
  pet?: boolean;
  /** Mastery tier of the killer's equipped camo on the weapon used (bronze..diamond); absent otherwise. */
  mastery?: string;
}

export interface ChatLine {
  id: number;
  name: string;
  team: number;
  text: string;
  system?: boolean;
}

export interface Announcement {
  id: number;
  text: string;
  sub?: string;
  color?: string;
  big?: boolean;
}

export interface SlotView {
  slot: number;
  name: string;
  num: number;
  ammo: number;
  reserve: number;
  mag: number;
  kind: string;
}

export interface MatchEndView {
  winnerTeam: number;
  winnerPlayer: number;
  scoreboard: ScoreLine[];
  mvp: number;
  result: MatchResult;
  won: boolean;
}

/** Our equipped pet for the HUD widget (from own state). */
export interface PetHud {
  num: number;
  id: string;
  name: string;
  role: string;
  /** 0..100 (0 while re-forming). */
  hpPct: number;
  /** Seconds until it re-forms (0 when present). */
  reformIn: number;
  /** Accent color (CSS) of the pet in its style. */
  color: string;
}

export type PetFxKind = 'hurt' | 'heal' | 'shield' | 'down' | 'up';

let seq = 1;
export const nextId = (): number => seq++;

export const hud = {
  active: signal(false),
  loading: signal<{ stage: string; progress: number } | null>(null),
  myId: signal(-1),
  myTeam: signal(0),
  settings: signal<MatchSettings | null>(null),
  players: signal<Map<number, PlayerInfo>>(new Map()),
  me: signal({ alive: false, hp: 100, maxHp: 100, armor: 0, maxArmor: 60, respawnIn: 0, blocks: 0, status: 0 }),
  weapon: signal({
    slot: 0,
    slots: [] as SlotView[],
    reloading: false,
    reloadLeft: 0,
    reloadTotal: 1,
    charging: false,
    gadget: { name: '', icon: '', cooldown: 0, max: 1 },
  }),
  mode: signal<Record<string, unknown>>({}),
  scoreboard: signal<ScoreLine[]>([]),
  killfeed: signal<KillFeedEntry[]>([]),
  chat: signal<ChatLine[]>([]),
  hitmarker: signal<{ id: number; head: boolean; kill: boolean; pet?: boolean } | null>(null),
  damage: signal<{ id: number; angle: number }[]>([]),
  announcements: signal<Announcement[]>([]),
  showScoreboard: signal(false),
  chatOpen: signal(false),
  menuOpen: signal(false),
  death: signal<{ killer: string; weapon: string; flags: number; killerId: number } | null>(null),
  matchEnd: signal<MatchEndView | null>(null),
  stats: signal({ fps: 0, ping: 0, tris: 0, calls: 0, debris: 0, pending: 0 }),
  scoped: signal(false),
  /** Reticle of the fitted dot/ring/chevron/cross sight while aiming (null = normal crosshair). */
  reticle: signal<'dot' | 'ring' | 'chevron' | 'cross' | null>(null),
  crosshairSpread: signal(1),
  onTarget: signal(false),
  /** In-match player ids whose chat is hidden locally. */
  muted: signal<Set<number>>(new Set()),
  /** Name of the player being spectated (no-respawn modes), or null. */
  spectating: signal<string | null>(null),
  lowHealth: signal(false),
  hurtFlash: signal(0),
  error: signal<string | null>(null),
  interact: signal<string | null>(null),
  /** Our pet (null = none equipped). */
  pet: signal<PetHud | null>(null),
  /** Latest feedback pulse for the pet widget (hurt, heal, shield absorb, destroyed, re-formed). */
  petFx: signal<{ id: number; kind: PetFxKind; value: number } | null>(null),
  /** We are marked by an enemy scout until this time (performance.now ms). */
  spottedUntil: signal(0),
};

export function announce(text: string, sub?: string, color?: string, big = false, ms = 2600): void {
  const a: Announcement = { id: nextId(), text, sub, color, big };
  hud.announcements.value = [...hud.announcements.value.slice(-2), a];
  setTimeout(() => {
    hud.announcements.value = hud.announcements.value.filter((x) => x.id !== a.id);
  }, ms);
}

export function pushChat(line: Omit<ChatLine, 'id'>): void {
  hud.chat.value = [...hud.chat.value.slice(-7), { ...line, id: nextId() }];
}

export function resetHud(): void {
  hud.killfeed.value = [];
  hud.chat.value = [];
  hud.announcements.value = [];
  hud.damage.value = [];
  hud.death.value = null;
  hud.matchEnd.value = null;
  hud.mode.value = {};
  hud.scoreboard.value = [];
  hud.error.value = null;
  hud.menuOpen.value = false;
  hud.chatOpen.value = false;
  hud.showScoreboard.value = false;
  hud.pet.value = null;
  hud.petFx.value = null;
  hud.spottedUntil.value = 0;
}
